import { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { toast } from "sonner"
import { DateRangePicker } from "../components/date-range-picker"
import { format, parseISO } from "date-fns"
import { useNavigate, useLocation } from "react-router-dom"
import { useAppSelector } from "../app/hooks"
import {
    fetchJobMatches,
    fetchJobDetail,
    fetchCandidateDetail,
    type CandidateDetail,
    type JobListItem,
    type JobMatch,
    type RecruiterMatchItem,
} from "../features/jobs/jobAPI"
import { ProfileImage } from "../components/ProfileImage"
import { StarRating } from "../components/Candidate-Sections/star-rating"
import DropdownChevron from "../components/dropdown-chevron"

/** Append new page items without duplicate match ids (guards against overlapping requests). */
function appendMatchesUnique(prev: JobMatch[], incoming: JobMatch[]): JobMatch[] {
    if (incoming.length === 0) return prev
    const seen = new Set(prev.map((m) => m.id))
    const out = [...prev]
    for (const m of incoming) {
        if (!seen.has(m.id)) {
            seen.add(m.id)
            out.push(m)
        }
    }
    return out
}

/** Whether more pages exist for this match_type after a fetch (aligns with API pagination). */
function computeHasMoreAfterPage(
    itemsLength: number,
    pageLimit: number,
    nextOffset: number,
    totalCount?: number
): boolean {
    const reachedEndByCount =
        typeof totalCount === "number" && totalCount >= 0 && nextOffset >= totalCount
    const reachedEndByPage = itemsLength === 0 || itemsLength < pageLimit
    return !(reachedEndByPage || reachedEndByCount)
}

const CandidateMatches = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const accessToken = useAppSelector((state) => state.auth.accessToken)
    const userGroups = useAppSelector((state) => state.auth.userGroups)

    // Get job ID and optional initial tab from location state
    const jobId = location.state?.jobId as number | undefined
    const initialTabFromState = location.state?.activeTab as string | undefined

    const [isPickerOpen, setIsPickerOpen] = useState(false)
    const [activeTab, setActiveTab] = useState(() => {
        const validTabs = ["In matches", "Incoming request", "In review", "Shortlisted"]
        return initialTabFromState && validTabs.includes(initialTabFromState)
            ? initialTabFromState
            : "In matches"
    })
    const [dateRange, setDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({
        from: undefined,
        to: undefined,
    })

    const [hasDateRangeSelected, setHasDateRangeSelected] = useState(false)
    const [normalMatches, setNormalMatches] = useState<JobMatch[]>([])
    const [extendedMatches, setExtendedMatches] = useState<JobMatch[]>([])
    const [jobDetails, setJobDetails] = useState<JobListItem | null>(null)
    const [candidateDetailsById, setCandidateDetailsById] = useState<Record<number, CandidateDetail>>({})
    const [isLoading, setIsLoading] = useState(false)
    const [isLoadingMatches, setIsLoadingMatches] = useState(false)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const PAGE_LIMIT = 5
    /** Refs are the source of truth for API offset (avoids stale closures after filter reset + microtask). */
    const normalOffsetRef = useRef(0)
    const extendedOffsetRef = useRef(0)
    const [hasMoreNormal, setHasMoreNormal] = useState(true)
    const [hasMoreExtended, setHasMoreExtended] = useState(true)
    const hasMoreNormalRef = useRef(true)
    const hasMoreExtendedRef = useRef(true)
    const activeMatchTypeRef = useRef<"normal" | "extended">("normal")
    const sentinelRef = useRef<HTMLDivElement | null>(null)
    /** Blocks concurrent loadMoreMatches (same offset requested twice from scroll + effects). */
    const pageFetchInFlightRef = useRef(false)
    /** Incremented when filters/job reset so stale responses are ignored. */
    const fetchGenerationRef = useRef(0)
    const candidateDetailRequestsRef = useRef<Set<number>>(new Set())

    // Helper function to check if a date is valid
    const isValidDate = (date: Date | undefined): date is Date => {
        return date instanceof Date && !isNaN(date.getTime())
    }

    // Get filters based on active tab and date range
    const getFilters = useCallback(() => {
        const filters: { match_stage?: string; time_range_start?: string; time_range_end?: string } = {}

        // Add date range filters only if user has manually selected a date range AND it's not "All time"
        if (hasDateRangeSelected && (dateRange.from !== undefined || dateRange.to !== undefined)) {
            if (isValidDate(dateRange.from)) {
                filters.time_range_start = format(dateRange.from, "yyyy-MM-dd")
            }
            if (isValidDate(dateRange.to)) {
                filters.time_range_end = format(dateRange.to, "yyyy-MM-dd")
            }
        }

        // Add match_stage based on active tab (In matches = no param)
        switch (activeTab) {
            case "In matches":
                break
            case "Incoming request":
                filters.match_stage = "Incoming request"
                break
            case "In review":
                filters.match_stage = "In review"
                break
            case "Shortlisted":
                filters.match_stage = "Shortlisted"
                break
            default:
                break
        }

        return filters
    }, [activeTab, dateRange, hasDateRangeSelected])

    // Fetch job details
    useEffect(() => {
        if (!accessToken || !jobId) return

        const loadData = async () => {
            try {
                setIsLoading(true)
                setError(null)

                const jobResponse = await fetchJobDetail(jobId, accessToken)
                if (jobResponse.data) {
                    setJobDetails(jobResponse.data)
                }
            } catch (err) {
                const message = err instanceof Error ? err.message : "Failed to load data."
                setError(message)
            } finally {
                setIsLoading(false)
            }
        }

        loadData()
    }, [jobId, accessToken])

    const isCompanyHiringManager = (userGroups ?? []).includes("company_hiring_manager")
    const tabs = isCompanyHiringManager
        ? [
            { label: "In matches" },
            { label: "In review" },
            { label: "Shortlisted" },
        ]
        : [
            { label: "In matches" },
            { label: "Incoming request" },
            { label: "In review" },
            { label: "Shortlisted" },
        ]

    const isInReviewTab = activeTab === "In review"
    const isShortlistedTab = activeTab === "Shortlisted"
    const isFlatTableTab = isInReviewTab || isShortlistedTab
    const columnCount = isShortlistedTab ? 6 : 5

    const combinedMatches = useMemo(
        () => appendMatchesUnique(normalMatches, extendedMatches),
        [normalMatches, extendedMatches]
    )

    const jobHeaderInfo = useMemo(() => {
        if (!jobDetails) {
            return { title: "", location: "", jobType: "", workType: "", hiringManager: "" }
        }

        const hiringManagerName = jobDetails.hiring_manager
            ? `${jobDetails.hiring_manager.first_name ?? ""} ${jobDetails.hiring_manager.middle_name ?? ""} ${jobDetails.hiring_manager.last_name ?? ""}`.trim() ||
              jobDetails.hiring_manager.name ||
              "N/A"
            : "N/A"

        return {
            title: jobDetails.job_title || "N/A",
            location: jobDetails.city?.name || "N/A",
            jobType: Array.isArray(jobDetails.employment_type)
                ? jobDetails.employment_type.map((e) => e.name).join(", ")
                : "N/A",
            workType: Array.isArray(jobDetails.workplace_type)
                ? jobDetails.workplace_type.map((w) => w.name).join(", ")
                : "N/A",
            hiringManager: hiringManagerName,
        }
    }, [jobDetails])

    const formatStatusForDisplay = (status: string): string =>
        status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())

    const getStageLabel = (match: JobMatch): string => {
        const stage = match.stage?.trim() ?? ""
        const stageLower = stage.toLowerCase()
        const statusRaw = match.recruiter_action?.trim() ?? ""
        const statusLower = statusRaw.toLowerCase()

        if (stageLower === "initial" && statusLower === "requested") {
            return "Awaiting Response"
        }

        if (stageLower === "verdict" || stageLower === "initial") {
            if (!statusRaw) return "—"
            return formatStatusForDisplay(statusRaw)
        }

        return stage || "—"
    }

    /** API `status` (mapped to `recruiter_action`) — bold row at any stage when it is New. */
    const isNewStatus = (match: JobMatch): boolean =>
        String(match.recruiter_action ?? "").toLowerCase() === "new"

    const newRowClassName =
        "[&_td]:!font-bold [&_td_p]:!font-bold [&_td_span]:!font-bold"

    const getAppliedDate = (match: JobMatch): string => {
        const raw = match.raw as { applied_ago?: string | null; applied_date?: string | null } | undefined
        const appliedAgo = raw?.applied_ago?.trim()
        if (appliedAgo) return appliedAgo

        const appliedDate = raw?.applied_date?.trim() || match.candidate_action_date?.trim()
        if (appliedDate) {
            try {
                const dateTime = appliedDate.includes("T")
                    ? appliedDate
                    : `${appliedDate}T00:00:00`
                const date = parseISO(dateTime)
                if (!Number.isNaN(date.getTime())) {
                    return format(date, "MMM d, yyyy")
                }
            } catch {
                return appliedDate
            }
            return appliedDate
        }

        return "N/A"
    }

    const getFlatTableStatusLabel = (match: JobMatch): string => {
        if (isShortlistedTab) return "Shortlisted"
        const stage = match.stage?.trim()
        if (stage) return stage
        return getStageLabel(match)
    }

    const getInterviewDateDisplay = (match: JobMatch): string => {
        const raw = match.raw as { interview_date?: string | null } | undefined
        const dateStr =
            match.recruiter_action_date?.trim() ||
            raw?.interview_date?.trim() ||
            ""
        if (!dateStr) return ""

        try {
            const dateTime = dateStr.includes("T") ? dateStr : `${dateStr}T00:00:00`
            const date = parseISO(dateTime)
            if (!Number.isNaN(date.getTime())) {
                return format(date, "M/d/yyyy")
            }
        } catch {
            return dateStr
        }
        return dateStr
    }

    /** First paint: load page 0 for both normal and extended together, then scroll loads more per type. */
    const loadInitialMatches = useCallback(async () => {
        if (!accessToken || !jobId) return
        if (pageFetchInFlightRef.current) return

        pageFetchInFlightRef.current = true
        setIsLoadingMatches(true)
        const generation = fetchGenerationRef.current
        try {
            const filters = getFilters()
            const [normalResp, extendedResp] = await Promise.all([
                fetchJobMatches(jobId, accessToken, {
                    ...filters,
                    match_type: "normal",
                    limit: PAGE_LIMIT,
                    offset: 0,
                }),
                fetchJobMatches(jobId, accessToken, {
                    ...filters,
                    match_type: "extended",
                    limit: PAGE_LIMIT,
                    offset: 0,
                }),
            ])

            if (generation !== fetchGenerationRef.current) {
                return
            }

            if (normalResp.error) {
                setError(normalResp.error)
                return
            }
            if (extendedResp.error) {
                setError(extendedResp.error)
                return
            }

            const normalItems = normalResp.data || []
            const extendedItems = extendedResp.data || []

            setNormalMatches(normalItems)
            setExtendedMatches(extendedItems)

            const normalTotal = normalResp.pagination?.count
            const extendedTotal = extendedResp.pagination?.count
            const normalNext = normalItems.length
            const extendedNext = extendedItems.length

            const normalHasMore = computeHasMoreAfterPage(
                normalItems.length,
                PAGE_LIMIT,
                normalNext,
                normalTotal
            )
            const extendedHasMore = computeHasMoreAfterPage(
                extendedItems.length,
                PAGE_LIMIT,
                extendedNext,
                extendedTotal
            )

            normalOffsetRef.current = normalNext
            extendedOffsetRef.current = extendedNext
            hasMoreNormalRef.current = normalHasMore
            hasMoreExtendedRef.current = extendedHasMore
            setHasMoreNormal(normalHasMore)
            setHasMoreExtended(extendedHasMore)
            activeMatchTypeRef.current = normalHasMore ? "normal" : "extended"
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to load matches."
            setError(message)
        } finally {
            pageFetchInFlightRef.current = false
            setIsLoadingMatches(false)
        }
    }, [accessToken, jobId, getFilters])

    const loadMoreMatches = useCallback(async () => {
        if (!accessToken || !jobId) return
        if (pageFetchInFlightRef.current) return
        if (!hasMoreNormalRef.current && !hasMoreExtendedRef.current) return

        pageFetchInFlightRef.current = true
        setIsLoadingMore(true)
        const generation = fetchGenerationRef.current
        try {
            let matchType: "normal" | "extended" = activeMatchTypeRef.current
            let offset =
                matchType === "normal" ? normalOffsetRef.current : extendedOffsetRef.current

            if (matchType === "normal" && !hasMoreNormalRef.current) {
                matchType = "extended"
                offset = extendedOffsetRef.current
                activeMatchTypeRef.current = "extended"
            }

            if (matchType === "extended" && !hasMoreExtendedRef.current) {
                return
            }

            const filters = getFilters()
            const response = await fetchJobMatches(jobId, accessToken, {
                ...filters,
                match_type: matchType,
                limit: PAGE_LIMIT,
                offset,
            })

            if (generation !== fetchGenerationRef.current) {
                return
            }

            if (response.error) {
                setError(response.error)
                return
            }

            const newItems = response.data || []
            const totalCount = response.pagination?.count

            if (matchType === "normal") {
                setNormalMatches((prev) => appendMatchesUnique(prev, newItems))
            } else {
                setExtendedMatches((prev) => appendMatchesUnique(prev, newItems))
            }

            const nextOffset = offset + newItems.length
            const noMoreForThisType = !computeHasMoreAfterPage(
                newItems.length,
                PAGE_LIMIT,
                nextOffset,
                totalCount
            )

            if (matchType === "normal") {
                normalOffsetRef.current = nextOffset
                if (noMoreForThisType) {
                    hasMoreNormalRef.current = false
                    setHasMoreNormal(false)
                    activeMatchTypeRef.current = "extended"
                }
            } else {
                extendedOffsetRef.current = nextOffset
                if (noMoreForThisType) {
                    hasMoreExtendedRef.current = false
                    setHasMoreExtended(false)
                }
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : "Failed to load matches."
            setError(message)
        } finally {
            pageFetchInFlightRef.current = false
            setIsLoadingMore(false)
        }
    }, [accessToken, jobId, getFilters])

    // Reset list + offsets when job or filters change, then load first page of normal + extended together.
    useEffect(() => {
        if (!accessToken || !jobId) return
        fetchGenerationRef.current += 1
        normalOffsetRef.current = 0
        extendedOffsetRef.current = 0
        hasMoreNormalRef.current = true
        hasMoreExtendedRef.current = true
        activeMatchTypeRef.current = "normal"
        setNormalMatches([])
        setExtendedMatches([])
        setHasMoreNormal(true)
        setHasMoreExtended(true)
        setError(null)
        void loadInitialMatches()
    }, [accessToken, jobId, activeTab, dateRange, hasDateRangeSelected, loadInitialMatches])

    useEffect(() => {
        const target = sentinelRef.current
        if (!target) return

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries
                if (entry.isIntersecting) {
                    loadMoreMatches()
                }
            },
            { root: null, rootMargin: "200px", threshold: 0.1 }
        )

        observer.observe(target)
        return () => observer.disconnect()
    }, [loadMoreMatches])

    const getRawMatchValue = (match: JobMatch, keys: string[]) => {
        const raw = match.raw as Record<string, any> | undefined
        for (const key of keys) {
            if ((match as any)[key] !== undefined && (match as any)[key] !== null) {
                return (match as any)[key]
            }
            if (raw && raw[key] !== undefined && raw[key] !== null) {
                return raw[key]
            }
        }
        return null
    }

    // Get candidate title (last job position and first expertise company)
    const getCandidateTitle = (match: JobMatch): string => {
        const parts: string[] = []
        const position =
            match.candidate.last_job_position ||
            getRawMatchValue(match, ["last_job_position"])
        const company =
            match.candidate.last_company ||
            getRawMatchValue(match, ["last_company"]) ||
            (() => {
                const raw = match.raw as RecruiterMatchItem | undefined
                const firstCompany = raw?.areas_of_expertise?.[0]?.companies?.[0]
                return typeof firstCompany === "string" && firstCompany.trim() ? firstCompany.trim() : null
            })()

        if (position) {
            parts.push(String(position))
        }
        if (company) {
            parts.push(String(company))
        }
        return parts.join(" • ") || "N/A"
    }

    const formatMatchScore = (score: string | number | null | undefined): string => {
        const parsedScore = Number.parseFloat(String(score ?? 0))
        return Number.isFinite(parsedScore) ? parsedScore.toFixed(0) : "0"
    }

    const loadCandidateDetailsForPopover = useCallback(
        async (candidateId: number) => {
            if (!accessToken || candidateDetailsById[candidateId] || candidateDetailRequestsRef.current.has(candidateId)) {
                return
            }

            candidateDetailRequestsRef.current.add(candidateId)
            try {
                const result = await fetchCandidateDetail(candidateId, accessToken)
                if (result.data) {
                    setCandidateDetailsById((prev) => ({
                        ...prev,
                        [candidateId]: result.data as CandidateDetail,
                    }))
                } else if (result.error) {
                    console.error("Failed to fetch candidate details:", result.error)
                }
            } catch (error) {
                console.error("Error fetching candidate details:", error)
            } finally {
                candidateDetailRequestsRef.current.delete(candidateId)
            }
        },
        [accessToken, candidateDetailsById]
    )

    const getCandidateExperience = (match: JobMatch, candidateDetail?: CandidateDetail): string => {
        return (
            candidateDetail?.years_of_professional_experience?.name ||
            match.candidate.years_of_experience?.name ||
            getRawMatchValue(match, ["relevant_experience", "candidate_experience", "years_of_experience"]) ||
            "N/A"
        )
    }

    const formatNameList = (items?: Array<{ name?: string } | null>): string => {
        const names = items?.map((item) => item?.name).filter(Boolean) ?? []
        return names.length > 0 ? names.join(", ") : "N/A"
    }

    const formatJobEducationRequirement = (): string => {
        const educationRows = jobDetails?.education ?? []
        const labels = educationRows
            .map((education) =>
                [education.qualification?.name, education.field_of_study?.name].filter(Boolean).join(", ")
            )
            .filter(Boolean)

        return labels.length > 0 ? labels.join(" • ") : "N/A"
    }

    const ratioToDots = (ratio: number): number => {
        const clamped = Math.min(Math.max(ratio, 0), 1)
        return Math.round(clamped * 5)
    }

    const normalizeExpertiseLabel = (value?: string | null) =>
        (value ?? "").trim().toLowerCase().replace(/[_-]+/g, " ")

    type JobExpertiseArea = NonNullable<JobListItem["areas_of_expertise"]>[number]

    const getJobAreaMustHaveSkills = (
        area?: JobExpertiseArea
    ): Array<{ id: number; name?: string }> => {
        const raw = area?.skills_must_have ?? (area as { must_have_skills?: unknown })?.must_have_skills
        if (!Array.isArray(raw)) return []
        return raw
            .map((item) => {
                if (typeof item === "number") return { id: item }
                if (item && typeof item === "object" && typeof (item as { id?: number }).id === "number") {
                    return { id: (item as { id: number }).id, name: (item as { name?: string }).name }
                }
                return null
            })
            .filter((item): item is { id: number; name?: string } => item !== null)
    }

    const jobAreaMatchesCandidateExpertise = (
        match: JobMatch,
        jobArea: JobExpertiseArea | undefined,
        candidateDetail?: CandidateDetail
    ) => {
        const requiredFieldId = jobArea?.field_of_expertise?.id
        const requiredFieldName = jobArea?.field_of_expertise?.name
        if (!requiredFieldId && !requiredFieldName) return false

        const candidateAreas = candidateDetail?.areas_of_expertise ?? []
        if (
            candidateAreas.some((area) => {
                if (requiredFieldId && area.field_of_expertise?.id === requiredFieldId) return true
                return (
                    !!requiredFieldName &&
                    normalizeExpertiseLabel(area.field_of_expertise?.name) ===
                        normalizeExpertiseLabel(requiredFieldName)
                )
            })
        ) {
            return true
        }

        if (
            requiredFieldId &&
            match.candidate.first_field_of_expertise?.id === requiredFieldId
        ) {
            return true
        }

        return (
            !!requiredFieldName &&
            normalizeExpertiseLabel(match.candidate.first_field_of_expertise?.name) ===
                normalizeExpertiseLabel(requiredFieldName)
        )
    }

    const getCandidateSkillsForJobExpertise = (
        candidateDetail: CandidateDetail | undefined,
        jobArea?: JobExpertiseArea
    ): { ids: Set<number>; names: Set<string> } => {
        const ids = new Set<number>()
        const names = new Set<string>()
        const requiredFieldId = jobArea?.field_of_expertise?.id
        const requiredFieldName = jobArea?.field_of_expertise?.name
        if (!candidateDetail?.areas_of_expertise?.length) return { ids, names }

        for (const area of candidateDetail.areas_of_expertise) {
            const idMatch =
                !!requiredFieldId && area.field_of_expertise?.id === requiredFieldId
            const nameMatch =
                !!requiredFieldName &&
                normalizeExpertiseLabel(area.field_of_expertise?.name) ===
                    normalizeExpertiseLabel(requiredFieldName)
            if (!idMatch && !nameMatch) continue

            for (const skill of area.key_skills ?? []) {
                if (typeof skill?.id === "number") ids.add(skill.id)
                const normalizedName = normalizeExpertiseLabel(skill?.name)
                if (normalizedName) names.add(normalizedName)
            }
        }

        return { ids, names }
    }

    const parseRawExpertiseScore = (value: unknown): number | null => {
        if (typeof value === "number" && Number.isFinite(value)) return value
        if (typeof value === "string") {
            const parsed = Number.parseFloat(value)
            return Number.isFinite(parsed) ? parsed : null
        }
        if (value && typeof value === "object") {
            const record = value as Record<string, unknown>
            for (const key of ["score", "match_score", "relevance", "value"]) {
                const parsed = parseRawExpertiseScore(record[key])
                if (parsed !== null) return parsed
            }
        }
        return null
    }

    const getRawExpertiseScoreForJobArea = (
        match: JobMatch,
        jobArea: JobExpertiseArea | undefined,
        index: number
    ): number | null => {
        const rawExpertiseScores = getRawMatchValue(match, [
            "areas_of_expertise_relevance",
            "area_of_expertise_relevance",
            "expertise_relevance",
            "expertise_scores",
        ])
        if (rawExpertiseScores == null) return null

        const fieldId = jobArea?.field_of_expertise?.id
        const fieldName = jobArea?.field_of_expertise?.name

        if (Array.isArray(rawExpertiseScores)) {
            const matchedEntry = rawExpertiseScores.find((entry) => {
                if (entry == null || typeof entry !== "object") return false
                const record = entry as Record<string, unknown>
                const entryField = record.field_of_expertise as
                    | { id?: number; name?: string }
                    | undefined
                const entryFieldId =
                    entryField?.id ??
                    (record.field_id as number | undefined) ??
                    (record.expertise_id as number | undefined)
                const entryFieldName =
                    entryField?.name ??
                    (record.field_name as string | undefined) ??
                    (record.expertise_name as string | undefined)
                if (fieldId != null && entryFieldId === fieldId) return true
                return (
                    !!fieldName &&
                    normalizeExpertiseLabel(entryFieldName) === normalizeExpertiseLabel(fieldName)
                )
            })
            const matchedScore = parseRawExpertiseScore(matchedEntry)
            if (matchedScore !== null) return matchedScore

            const indexEntry = rawExpertiseScores[index]
            return parseRawExpertiseScore(indexEntry)
        }

        if (typeof rawExpertiseScores === "object") {
            const record = rawExpertiseScores as Record<string, unknown>
            if (fieldId != null) {
                const byId = parseRawExpertiseScore(record[fieldId] ?? record[String(fieldId)])
                if (byId !== null) return byId
            }
            if (fieldName) {
                const byName = parseRawExpertiseScore(record[fieldName])
                if (byName !== null) return byName
            }
            const byAreaKey = parseRawExpertiseScore(
                record[`area_${index + 1}`] ?? record[index]
            )
            if (byAreaKey !== null) return byAreaKey
        }

        return parseRawExpertiseScore(rawExpertiseScores)
    }

    const getExpertiseDotScore = (
        match: JobMatch,
        index: number,
        candidateDetail?: CandidateDetail
    ): number => {
        const jobArea = jobDetails?.areas_of_expertise?.[index]
        const requiredExpertiseId = jobArea?.field_of_expertise?.id
        const requiredExpertiseName = jobArea?.field_of_expertise?.name
        const mustHave = getJobAreaMustHaveSkills(jobArea)

        const rawScore = getRawExpertiseScoreForJobArea(match, jobArea, index)

        const candidateHasExpertise = jobAreaMatchesCandidateExpertise(match, jobArea, candidateDetail)

        if (candidateDetail && (requiredExpertiseId || requiredExpertiseName) && mustHave.length > 0) {
            if (!candidateHasExpertise) return 0
            const { ids: candidateSkillIds, names: candidateSkillNames } =
                getCandidateSkillsForJobExpertise(candidateDetail, jobArea)
            const matched = mustHave.filter((skill) => {
                if (candidateSkillIds.has(skill.id)) return true
                const skillName = normalizeExpertiseLabel(skill.name)
                return Boolean(skillName) && candidateSkillNames.has(skillName)
            }).length
            return ratioToDots(matched / mustHave.length)
        }

        if (rawScore !== null) {
            return Math.round(Math.min(Math.max(rawScore, 0), 100) / 20)
        }

        if (!requiredExpertiseId && !requiredExpertiseName) return 0
        if (mustHave.length === 0) {
            return candidateHasExpertise ? 5 : 0
        }

        if (!candidateDetail) return 0
        return 0
    }

    const getEducationMatchRatioForRow = (
        jobRow: NonNullable<JobListItem["education"]>[number],
        candidateEducation: NonNullable<CandidateDetail["education"]>
    ): number => {
        const needQual = !!jobRow.qualification?.id
        const needField = !!jobRow.field_of_study?.id
        if (!needQual && !needField) return 1

        const parts: number[] = []
        if (needQual) {
            const hit = candidateEducation.some(
                (education) => education.qualification?.id === jobRow.qualification?.id
            )
            parts.push(hit ? 1 : 0)
        }
        if (needField) {
            const hit = candidateEducation.some(
                (education) => education.field_of_study?.id === jobRow.field_of_study?.id
            )
            parts.push(hit ? 1 : 0)
        }
        return parts.reduce((a, b) => a + b, 0) / parts.length
    }

    const getEducationDotScore = (match: JobMatch, candidateDetail?: CandidateDetail): number => {
        const rawEducationScore = getRawMatchValue(match, [
            "education_relevance",
            "education_match_score",
            "education_score",
        ])

        const jobEducation = jobDetails?.education ?? []
        const candidateEducation = candidateDetail?.education ?? []

        if (candidateDetail && jobEducation.length > 0 && candidateEducation.length > 0) {
            const ratios = jobEducation.map((row) => getEducationMatchRatioForRow(row, candidateEducation))
            const avg = ratios.reduce((a, b) => a + b, 0) / ratios.length
            return ratioToDots(avg)
        }

        if (typeof rawEducationScore === "number") {
            return Math.round(Math.min(Math.max(rawEducationScore, 0), 100) / 20)
        }

        const firstRequirement = jobEducation[0]
        const qualificationMatches =
            !!firstRequirement?.qualification?.id &&
            (firstRequirement.qualification.id === match.candidate.first_qualification?.id ||
                candidateEducation.some(
                    (education) => education.qualification?.id === firstRequirement.qualification?.id
                ))
        const fieldMatches =
            !!firstRequirement?.field_of_study?.id &&
            (firstRequirement.field_of_study.id === match.candidate.first_field_of_study?.id ||
                candidateEducation.some(
                    (education) => education.field_of_study?.id === firstRequirement.field_of_study?.id
                ))

        if (qualificationMatches && (!firstRequirement?.field_of_study?.id || fieldMatches)) return 5
        if (qualificationMatches || fieldMatches) return 3
        return 0
    }

    const renderMatchDots = (filledCount: number) => (
        <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((dot) => (
                <span
                    key={dot}
                    className={`h-3 w-3 rounded-md ${dot <= filledCount ? "bg-[#A8E000]" : "bg-gray-200"}`}
                />
            ))}
        </div>
    )

    const MatchScorePopover = ({ match }: { match: JobMatch }) => {
        const score = formatMatchScore(match.overall_score)
        const candidateDetail = candidateDetailsById[match.candidate.id]
        const jobExpertiseRows = jobDetails?.areas_of_expertise ?? []
        const expertiseRows =
            jobExpertiseRows.length > 0
                ? jobExpertiseRows.map((expertise, index) => {
                    const expertiseName = expertise.field_of_expertise?.name
                    return {
                        label: `Area of Expertise ${index + 1}${expertiseName ? ` (${expertiseName})` : ""}`,
                        subLabel: `Must have: ${formatNameList(getJobAreaMustHaveSkills(expertise))}`,
                        score: getExpertiseDotScore(match, index, candidateDetail),
                    }
                })
                : [
                    {
                        label: "Area of Expertise",
                        subLabel: "Must have: N/A",
                        score: 0,
                    },
                ]

        return (
            <div className="pointer-events-none fixed inset-0 z-50 hidden items-center justify-center bg-black/10 p-4 group-hover/score:flex">
                <div className="w-[360px] rounded-md bg-white p-5 text-left shadow-[0_10px_35px_rgba(0,0,0,0.18)] ring-1 ring-black/5">
                    <div className="mb-5 grid grid-cols-2 gap-6">
                        <div>
                            <p className="text-xl font-bold text-gray-900">{getCandidateExperience(match, candidateDetail)}</p>
                            <p className="text-xs font-semibold text-gray-700">Relevant experience</p>
                        </div>
                        <div>
                            <p className="text-xl font-bold text-gray-900">{score}%</p>
                            <p className="text-xs font-semibold text-gray-700">Ideal candidate</p>
                        </div>
                    </div>

                    <div className="space-y-3">
                        {expertiseRows.map((row) => (
                            <div key={row.label} className="grid grid-cols-[1fr_auto] items-center gap-4">
                                <div className="min-w-0">
                                    <p className="text-sm font-bold text-gray-900">{row.label}</p>
                                    <p className="truncate text-xs font-medium text-gray-600">{row.subLabel}</p>
                                </div>
                                {renderMatchDots(row.score)}
                            </div>
                        ))}

                        <div className="grid grid-cols-[1fr_auto] items-center gap-4">
                            <div className="min-w-0">
                                <p className="text-sm font-bold text-gray-900">Education relevance</p>
                                <p className="truncate text-xs font-medium text-gray-600">
                                    {formatJobEducationRequirement()}
                                </p>
                            </div>
                            {renderMatchDots(getEducationDotScore(match, candidateDetail))}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const getStatusForIndicator = (match: JobMatch): string => {
        const raw =
            (match.recruiter_action && typeof match.recruiter_action === "string"
                ? match.recruiter_action
                : match.candidate_action && typeof match.candidate_action === "string"
                    ? match.candidate_action
                    : "") || ""
        return raw.toLowerCase()
    }

    const getStageForIndicator = (match: JobMatch): string | null => {
        return (match as any).stage ? String((match as any).stage) : null
    }

    const getIndicatorClasses = (match: JobMatch): string | null => {
        const stage = getStageForIndicator(match)
        const status = getStatusForIndicator(match)

        // Hollow green: Initial / no stage
        if (!stage || stage === "Initial" || stage === "In near") {
            return "h-3 w-3 rounded-md border-2 border-[#ABD405] bg-white"
        }

        // Solid green: Incoming request, In review, Shortlisted
        if (stage === "Incoming request" || stage === "In review" || stage === "Shortlisted") {
            return "h-3 w-3 rounded-md bg-[#ABD405]"
        }

        // Verdict: declined = solid red; rejected = small hollow red
        if (stage === "Verdict") {
            if (status.includes("declined")) {
                return "h-3 w-3 rounded-md border-2 border-[#D40C05] bg-white"
            }
            if (status.includes("rejected")) {
                return "h-3 w-3 rounded-md bg-[#D40C05]"
            }
            if (status.includes("hired")) {
                return "h-3 w-3 rounded-md bg-[#ABD405]"
            }
        }

        return null
    }

    const handleCandidateClick = async (candidateId: number) => {
        if (!accessToken) return

        try {
            // Fetch candidate details before navigating
            const result = await fetchCandidateDetail(candidateId, accessToken)
            if (result.error) {
                console.error("Failed to fetch candidate details:", result.error)
                toast.error(`Failed to load candidate details: ${result.error}`)
                return
            }

            if (result.data) {
                // Navigate with candidate data, matches, and jobId in location state
                navigate(`/candidate/${candidateId}`, {
                    state: {
                        candidateData: result.data,
                        matches: combinedMatches,
                        jobId: jobId,
                    },
                })
            }
        } catch (error) {
            console.error("Error fetching candidate details:", error)
            toast.error("An error occurred while loading candidate details.")
        }
    }

    const handleOverviewClick = () => {
        if (jobId) {
            navigate(`/jobs/${jobId}`)
        }
    }

    const sharedHeaderCellClass = "bg-[#CDD6DF] px-4 py-4 text-sm font-bold whitespace-nowrap"
    const sharedBodyCellClass = "px-6 py-5 align-middle text-small font-normal text-gray-800"

    const MatchTableBlock = ({
        title,
        showTitle,
        rows,
    }: {
        title: string
        showTitle: boolean
        rows: JobMatch[]
    }) => (
        <div className={`mb-8 rounded-md pt-2 ${isFlatTableTab ? "" : "bg-[#F1F1F1]"}`}>
            {showTitle && (
                <h2 className="text-paragraph pb-2 font-bold text-black ml-3">{title}</h2>
            )}
            <div className="overflow-hidden rounded-md">
                <table className="w-full table-fixed border-collapse">
                    <colgroup>
                        {isShortlistedTab ? (
                            <>
                                <col className="w-[32%]" />
                                <col className="w-[13%]" />
                                <col className="w-[12%]" />
                                <col className="w-[13%]" />
                                <col className="w-[13%]" />
                                <col className="w-[17%]" />
                            </>
                        ) : (
                            <>
                                <col className="w-[36%]" />
                                <col className="w-[14%]" />
                                <col className="w-[14%]" />
                                <col className="w-[18%]" />
                                <col className="w-[18%]" />
                            </>
                        )}
                    </colgroup>
                    <thead>
                        <tr className="bg-[#D5DEE5]">
                            <th className={`${sharedHeaderCellClass} rounded-l-md text-left`}>
                                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                                    Candidate <span className="text-gray-500">▾</span>
                                </span>
                            </th>
                            <th className={`${sharedHeaderCellClass} text-center`}>
                                <span className="inline-flex items-center justify-center gap-1 whitespace-nowrap">
                                    Match Score <span className="text-gray-500">▾</span>
                                </span>
                            </th>
                            <th className={`${sharedHeaderCellClass} text-center`}>
                                <span className="inline-flex items-center justify-center gap-1 whitespace-nowrap">
                                    Applied <span className="text-gray-500">▾</span>
                                </span>
                            </th>
                            <th className={`${sharedHeaderCellClass} text-center`}>
                                <span className="inline-flex items-center justify-center gap-1 whitespace-nowrap">
                                    {isFlatTableTab ? "Status" : "Stage"}{" "}
                                    <span className="text-gray-500">▾</span>
                                </span>
                            </th>
                            {isShortlistedTab && (
                                <th className={`${sharedHeaderCellClass} text-center`}>
                                    <span className="inline-flex items-center justify-center gap-1 whitespace-nowrap">
                                        Date <span className="text-gray-500">▾</span>
                                    </span>
                                </th>
                            )}
                            <th
                                className={`${sharedHeaderCellClass} rounded-r-md text-center`}
                            >
                                Feedback
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {rows.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={columnCount}
                                    className="px-6 py-8 text-center text-sm text-gray-500"
                                >
                                    No matches found.
                                </td>
                            </tr>
                        ) : (
                            rows.map((match) => (
                                <tr
                                    key={match.id}
                                    className={`group hover:bg-gray-50 cursor-pointer transition ${isNewStatus(match) ? newRowClassName : ""}`}
                                    onClick={() => handleCandidateClick(match.candidate.id)}
                                >
                                    <td className={sharedBodyCellClass}>
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="relative">
                                                <ProfileImage
                                                    profilePicture={match.candidate.profile_picture}
                                                    profilePictureUrl={match.candidate.profile_picture_url}
                                                    accessToken={accessToken || undefined}
                                                    alt={match.candidate.name}
                                                    className="min-h-10 min-w-10 max-h-10 max-w-10 rounded-md bg-gray-200 border-2 border-black"
                                                    fallbackPath="/assets/images/Avatar2.jpg"
                                                />
                                                {getIndicatorClasses(match) && (
                                                    <span
                                                        className={`absolute -bottom-0.5 -right-0.5 ${getIndicatorClasses(
                                                            match
                                                        )}`}
                                                    />
                                                )}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-black">
                                                    {match.candidate.name}
                                                </p>
                                                <p className="text-tiny leading-snug text-gray-600 break-words">
                                                    {getCandidateTitle(match)}
                                                </p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className={`${sharedBodyCellClass} relative text-center`}>
                                        <div
                                            className="group/score relative inline-flex cursor-default items-center justify-center"
                                            onMouseEnter={() => loadCandidateDetailsForPopover(match.candidate.id)}
                                            onFocus={() => loadCandidateDetailsForPopover(match.candidate.id)}
                                        >
                                            <span>
                                                {formatMatchScore(match.overall_score)}%
                                            </span>
                                            <MatchScorePopover match={match} />
                                        </div>
                                    </td>
                                    <td className={`${sharedBodyCellClass} text-center`}>
                                        <span className="block truncate text-gray-600">
                                            {getAppliedDate(match)}
                                        </span>
                                    </td>
                                    <td className={`${sharedBodyCellClass} text-center`}>
                                        <span className="block truncate">
                                            {isFlatTableTab
                                                ? getFlatTableStatusLabel(match)
                                                : getStageLabel(match)}
                                        </span>
                                    </td>
                                    {isShortlistedTab && (
                                        <td className={`${sharedBodyCellClass} text-center`}>
                                            <span className="block truncate text-gray-600">
                                                {getInterviewDateDisplay(match)}
                                            </span>
                                        </td>
                                    )}
                                    <td className={sharedBodyCellClass}>
                                        <div className="flex justify-center">
                                            <StarRating rating={match.feedback || 0} />
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )

    return (
        <div className="min-h-screen bg-white p-5">
            <div className="mx-auto max-w-6xl">
                {/* Header Section */}
                <div className="mb-8 flex items-start justify-between">
                    <div className="flex flex-col">
                        <h1 className="text-heading font-bold text-gray-900">
                            {isLoading ? "Loading..." : jobHeaderInfo.title}
                        </h1>
                        {!isLoading && jobDetails && (
                            <>
                                <p className="text-paragraph -mt-2 text-black">
                                    {jobHeaderInfo.location} - {jobHeaderInfo.jobType.toLowerCase()} - {jobHeaderInfo.workType.toLowerCase()}
                                </p>
                                <p className="text-paragraph">
                                    <span className="font-medium text-gray-400">Hiring manager - </span>
                                    <span className="font-bold text-black">{jobHeaderInfo.hiringManager}</span>
                                </p>
                            </>
                        )}
                    </div>

                    <button
                        onClick={handleOverviewClick}
                        className="btn-cta rounded-md px-6 py-2 text-tiny font-normal"
                    >
                        Job overview
                    </button>
                </div>

                {/* Filters and Time Range Section */}
                <div className="mb-6 flex items-end justify-between">
                    <div className="flex gap-3">
                        {tabs.map((tab, index) => (
                            <button
                                key={index}
                                onClick={() => setActiveTab(tab.label)}
                                className={`flex items-center gap-2 rounded-md border border-gray-300 px-5 py-2.5 text-sm font-semibold transition-colors ${activeTab === tab.label ? "bg-black text-white" : "bg-white text-gray-700 hover:bg-gray-50"
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    <div className="flex flex-col items-start gap-2">
                        <label htmlFor="timeRange" className="text-sm font-bold text-black">
                            Date range
                        </label>
                        <button
                            id="timeRange"
                            onClick={() => setIsPickerOpen(true)}
                            className="flex w-56 font-medium items-center justify-between rounded-md bg-[#F1F1F1] px-4 py-2 text-sm text-black hover:bg-[#e5e5e5] transition-colors"
                        >
                            <span>
                                {isValidDate(dateRange.from) && isValidDate(dateRange.to)
                                    ? `${format(dateRange.from, "MM/dd/yyyy")} - ${format(dateRange.to, "MM/dd/yyyy")}`
                                    : "Select date range"
                                }
                            </span>
                            <DropdownChevron isOpen={isPickerOpen} />
                        </button>
                    </div>
                </div>

                {isPickerOpen && (
                    <DateRangePicker
                        isOpen={isPickerOpen}
                        onClose={() => setIsPickerOpen(false)}
                        onUpdate={(range) => {
                            // Check if "All time" is selected (both dates are undefined)
                            if (range.from === undefined && range.to === undefined) {
                                setDateRange({ from: undefined, to: undefined })
                                setHasDateRangeSelected(false)
                            } else if (range.from && range.to && isValidDate(range.from) && isValidDate(range.to)) {
                                setDateRange({ from: range.from, to: range.to })
                                setHasDateRangeSelected(true)
                            }
                            setIsPickerOpen(false)
                        }}
                        initialRange={dateRange}
                    />
                )}

                {/* In matches: keep separate tables. Other tabs: merge into one Matches table. */}
                {isLoadingMatches &&
                normalMatches.length === 0 &&
                extendedMatches.length === 0 ? (
                    <div
                        className={`mb-8 rounded-md pt-2 ${isFlatTableTab ? "" : "bg-[#F1F1F1]"}`}
                    >
                        <div className="overflow-hidden rounded-md">
                            <table className="w-full border-collapse">
                                <tbody>
                                    <tr>
                                        <td
                                            colSpan={columnCount}
                                            className="px-6 py-8 text-center text-sm text-gray-500"
                                        >
                                            Loading matches...
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : error ? (
                    <div className="mb-8 rounded-md px-6 py-8 text-center text-sm text-red-500">
                        {error}
                    </div>
                ) : (
                    <>
                        {activeTab === "In matches" ? (
                            <>
                                <MatchTableBlock
                                    title="Matches"
                                    showTitle={!isFlatTableTab}
                                    rows={normalMatches}
                                />
                                <MatchTableBlock
                                    title="Extended Matches"
                                    showTitle={true}
                                    rows={extendedMatches}
                                />
                            </>
                        ) : (
                            <MatchTableBlock
                                title="Matches"
                                showTitle={!isFlatTableTab}
                                rows={combinedMatches}
                            />
                        )}
                    </>
                )}

                {!isLoadingMatches && !error && isLoadingMore && (
                    <div className="py-4 text-center text-sm text-gray-500">Loading more matches...</div>
                )}
                {!isLoadingMatches &&
                    !error &&
                    !hasMoreNormal &&
                    !hasMoreExtended &&
                    combinedMatches.length > 0 && (
                    <div className="py-4 text-center text-sm text-gray-500">No more matches.</div>
                )}
                <div ref={sentinelRef} className="h-2 w-full" />
            </div>
        </div>
    )
}

export default CandidateMatches